import { FrameCapProps, FrameLayout } from './dynamicModels/FrameLayout';
import { useBoundingBoxSize } from './dynamicModels/frameUtils';
import { useK3Model } from './dynamicModels/useK3Model';
import { K3Model, useTypedSelector } from '@k3/core';
import { BBAnchor } from '@react-three/drei';
import { createContext, Suspense, useContext, useMemo, useRef } from 'react';
import { CatmullRomCurve3, Group, Matrix4, Vector3, Vector3Tuple } from 'three';
import { Model } from '@k3/core/dist/src/lib/types/model.interface';
import { useK3ModelAndAction } from './dynamicModels/useK3ModelAndAction';

export const getCableSubmodelKeys = (model: Model): string[] => {
  const props = model?.props as DynamicCableChainExtraProps;

  return [
    props.startCapKey ?? '',
    props.middleCapKey ?? '',
    props.endCapKey ?? '',
  ].filter((key) => !!key);
};

const MiddleFrame = ({ length }: { length: number }) => {
  const { middleCapKey = '' } = useContext(CableChainContext) ?? {};
  const { loadedModel, model } = useK3Model(middleCapKey);

  const size = useBoundingBoxSize(loadedModel?.scene);
  const isLoading = useTypedSelector((state) => state.k3d.isSceneLoading);

  const prevModels = useRef<Group[]>();

  const height = size?.y;
  const models = useMemo(() => {
    if (!loadedModel || !size || height === 0 || isLoading) {
      return prevModels.current;
    }

    const curve = new CatmullRomCurve3(
      [
        new Vector3(0, length / 2 + height / 4, 0),
        new Vector3(0, length / 2 - length / 8, 0),
        new Vector3(0, length / 2 - length / 3, 0.075),
        new Vector3(0, -length / 2 + length / 3, -0.05),
        new Vector3(0, -length / 2 + length / 8, 0),
        new Vector3(0, -length / 2 + height, 0),
      ],
      false,
      'catmullrom',
      0.5
    );

    const curveLength = curve.getLength();
    const curveSegments = Math.round(curveLength / height);
    const points = curve.getSpacedPoints(curveSegments);
    const frames = curve.computeFrenetFrames(curveSegments, false);

    const matrixes = frames.tangents.map((tangent, index) => {
      const matrix = new Matrix4();
      matrix.lookAt(new Vector3(), tangent, frames.binormals[index]);
      matrix.setPosition(points[index]);

      return matrix;
    });

    const models = matrixes.map((matrix) => {
      const box = loadedModel?.scene.clone();
      box.rotateX(Math.PI / 2);
      box.applyMatrix4(matrix);
      return box;
    });

    prevModels.current = models;
    return models;
  }, [isLoading, length, height, loadedModel]);

  return (
    <BBAnchor anchor={[0, 0, 0]}>
      <group
        userData={{
          modelId: model?.id,
        }}
      >
        {models?.map((box, index) => (
          <mesh key={index}>
            <primitive object={box} />
          </mesh>
        ))}
      </group>
    </BBAnchor>
  );
};

const StartCap = ({ onSizeChange }: FrameCapProps) => {
  const ref = useRef<Group>();
  useBoundingBoxSize(ref.current, onSizeChange);

  const { startCapKey = '' } = useContext(CableChainContext) ?? {};
  const { model, modelAction, component } = useK3ModelAndAction(startCapKey);

  if (!model || !modelAction) {
    return null;
  }

  return (
    <BBAnchor anchor={[0, 1, 0]}>
      <group
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        // rotation={[0, 0, -Math.PI / 2]}
      >
        <K3Model
          model={model}
          modelAction={modelAction}
          meshProps={modelAction?.props}
          fileURL={model.path}
          component={component}
        />
      </group>
    </BBAnchor>
  );
};

const EndCap = ({ onSizeChange }: FrameCapProps) => {
  const ref = useRef<Group>();
  const cornerCapSize = useBoundingBoxSize(ref.current);
  onSizeChange(cornerCapSize);

  const { endCapKey = '' } = useContext(CableChainContext) ?? {};
  const { model, modelAction, component } = useK3ModelAndAction(endCapKey);

  if (!model || !modelAction) {
    return null;
  }

  return (
    <BBAnchor anchor={[0, -1, 0]}>
      <group
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        // rotation={[0, 0, -Math.PI / 2]}
      >
        <K3Model
          model={model}
          modelAction={modelAction}
          meshProps={modelAction?.props}
          fileURL={model.path}
          component={component}
        />
      </group>
    </BBAnchor>
  );
};

export type DynamicCableChainExtraProps = {
  startCapKey?: string;
  middleCapKey?: string;
  endCapKey?: string;
};

const CableChainContext = createContext<DynamicCableChainExtraProps>({
  startCapKey: undefined,
  middleCapKey: undefined,
  endCapKey: undefined,
});

export const CableChain = ({
  width = 1,
  height = 1,
  depth = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  startCapKey,
  middleCapKey,
  endCapKey,
}: {
  width?: number;
  height?: number;
  depth?: number;
  position?: Vector3Tuple;
  rotation?: Vector3Tuple;
  scale?: Vector3Tuple;
} & DynamicCableChainExtraProps) => {
  const contextValue = useMemo(() => {
    return {
      startCapKey,
      middleCapKey,
      endCapKey,
    };
  }, [startCapKey, middleCapKey, endCapKey]);

  return (
    <group position={position} rotation={rotation} scale={scale}>
      <Suspense fallback={null}>
        <CableChainContext.Provider value={contextValue}>
          <FrameLayout
            axis={'y'}
            debug={false}
            width={width}
            height={height}
            depth={depth}
            MiddleCap={MiddleFrame}
            StartCap={StartCap}
            EndCap={EndCap}
          />
        </CableChainContext.Provider>
      </Suspense>
    </group>
  );
};
